import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Button,
  Fab,
  Card,
  CardContent,
  Grid,
  Chip,
  Snackbar,
  Collapse,
  IconButton,
  Divider
} from '@mui/material';
import { 
  Add as AddIcon, 
  TrendingUp as TrendingUpIcon,
  Pending as PendingIcon,
  CheckCircle as ApprovedIcon,
  Redeem as FulfilledIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { Prize, Team } from '../../types';
import { prizeService } from '../../services/prizeService';
import { teamService } from '../../services/teamService';
import { pointsService } from '../../services/pointsService';
import IndividualPrizesTab from './IndividualPrizesTab';
import TeamPrizesTab from './TeamPrizesTab';
import MyRedemptionsTab from './MyRedemptionsTab';
import CoachRedemptionsTab from './CoachRedemptionsTab';
import CreatePrizeDialog from './CreatePrizeDialog';

const PrizesPage: React.FC = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [individualPrizes, setIndividualPrizes] = useState<Prize[]>([]);
  const [teamPrizes, setTeamPrizes] = useState<Prize[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [createPrizeOpen, setCreatePrizeOpen] = useState(false);
  const [userTeams, setUserTeams] = useState<Team[]>([]);
  const [pointsSummary, setPointsSummary] = useState<any>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [contestRulesOpen, setContestRulesOpen] = useState(false);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      let teamsData: Team[] = [];
      let currentTeamId: string | null = null;

      console.log('PrizesPage: Loading data for user:', user.id, 'role:', user.role);

      if (user.role === 'coach' || user.role === 'headCoach') {
        // First ensure coach/head coach is added as member of their own teams
        await teamService.ensureCoachMembership(user.id);
        
        // For coaches/headCoaches, check both coach-owned teams and team memberships
        const [coachTeams, memberTeams] = await Promise.all([
          teamService.getTeamsByCoach(user.id),
          teamService.getTeamsByAthlete(user.id)
        ]);
        
        // Combine and deduplicate teams
        const allTeams = [...coachTeams];
        memberTeams.forEach(memberTeam => {
          if (!allTeams.find(team => team.id === memberTeam.id)) {
            allTeams.push(memberTeam);
          }
        });
        
        teamsData = allTeams;
        currentTeamId = teamsData.length > 0 ? teamsData[0].id : null;
        console.log('PrizesPage: Coach teams:', coachTeams.length, 'member teams:', memberTeams.length, 'total:', teamsData.length, 'currentTeamId:', currentTeamId);
        console.log('PrizesPage: Coach teams data:', teamsData);
      } else {
        // For athletes, get their team memberships
        teamsData = await teamService.getTeamsByAthlete(user.id);
        currentTeamId = teamsData.length > 0 ? teamsData[0].id : null;
        console.log('PrizesPage: Athlete teams:', teamsData.length, 'currentTeamId:', currentTeamId);
        console.log('PrizesPage: Athlete teams data:', teamsData);
      }

      setUserTeams(teamsData);

      if (!currentTeamId) {
        console.log('PrizesPage: No team found for user');
        setLoading(false);
        return;
      }

      // Load prizes, redemptions (for remaining count), and points for the current team
      console.log('PrizesPage: Loading prizes for team:', currentTeamId);
      const [individualPrizesData, teamPrizesData, redemptionsData, pointsData] = await Promise.all([
        prizeService.getIndividualPrizes(currentTeamId).catch((err) => {
          console.error('PrizesPage: Error loading individual prizes:', err);
          return [];
        }),
        prizeService.getTeamPrizes(currentTeamId).catch((err) => {
          console.error('PrizesPage: Error loading team prizes:', err);
          return [];
        }),
        prizeService.getRedemptionsByTeam(currentTeamId).catch((err) => {
          console.error('PrizesPage: Error loading redemptions for remaining count:', err);
          return [];
        }),
        pointsService.getPointsSummary(user.id, currentTeamId).catch((err) => {
          console.error('PrizesPage: Error loading points summary:', err);
          return null;
        })
      ]);

      // Compute remaining quantity from fulfilled redemptions (source of truth)
      const fulfilledCountByPrizeId: Record<string, number> = {};
      redemptionsData.filter((r: { status: string }) => r.status === 'fulfilled').forEach((r: { prizeId: string }) => {
        fulfilledCountByPrizeId[r.prizeId] = (fulfilledCountByPrizeId[r.prizeId] || 0) + 1;
      });
      const applyRemaining = (prize: Prize) => {
        if (prize.maxQuantity != null) {
          const fulfilled = fulfilledCountByPrizeId[prize.id] || 0;
          prize.currentQuantity = Math.max(0, (prize.maxQuantity ?? 0) - fulfilled);
        }
      };
      individualPrizesData.forEach(applyRemaining);
      teamPrizesData.forEach(applyRemaining);

      console.log('PrizesPage: Loaded prizes - individual:', individualPrizesData.length, 'team:', teamPrizesData.length);
      console.log('PrizesPage: Individual prizes data:', individualPrizesData);
      console.log('PrizesPage: Team prizes data:', teamPrizesData);
      setIndividualPrizes(individualPrizesData);
      setTeamPrizes(teamPrizesData);
      setPointsSummary(pointsData);
    } catch (err: any) {
      console.error('Error loading prizes data:', err);
      setError(err.message || 'Failed to load prizes data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handlePrizeCreated = () => {
    setCreatePrizeOpen(false);
    loadData(); // Reload to show new prize
  };

  const handlePrizeRedeemed = () => {
    setSuccessMessage('Prize redemption successful! Check "My Redemptions" tab for status updates.');
    loadData();
  };

  const isCoach = user?.role === 'coach' || user?.role === 'headCoach';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (userTeams.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        You need to be part of a team to view prizes.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Prizes & Awards
        </Typography>
        {isCoach && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreatePrizeOpen(true)}
          >
            Create Prize
          </Button>
        )}
      </Box>

      {/* Points Summary */}
      {pointsSummary && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary" gutterBottom>
                    {pointsSummary.totalPoints}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Points Earned
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main" gutterBottom>
                    {pointsSummary.availablePoints}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Available for Prizes
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="secondary.main" gutterBottom>
                    {pointsSummary.spentPoints}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Points Spent
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Chip
                    icon={<TrendingUpIcon />}
                    label={`Rank #${pointsSummary.rank}`}
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: '1rem', py: 2 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Individual Items" />
          <Tab label="Team Items" />
          <Tab label="My Redemptions" />
          {isCoach && <Tab label="Manage Redemptions" />}
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <IndividualPrizesTab
          prizes={individualPrizes}
          teamId={userTeams.length > 0 ? userTeams[0].id : null}
          onPrizeRedeemed={handlePrizeRedeemed}
          canManagePrizes={isCoach}
          coachId={user?.id ?? ''}
          onPrizeUpdated={loadData}
        />
      )}

      {tabValue === 1 && (
        <TeamPrizesTab
          prizes={teamPrizes}
          teamId={userTeams.length > 0 ? userTeams[0].id : null}
          onPrizeRedeemed={handlePrizeRedeemed}
          canManagePrizes={isCoach}
          coachId={user?.id ?? ''}
          onPrizeUpdated={loadData}
        />
      )}

      {tabValue === 2 && (
        <MyRedemptionsTab
          onRedemptionUpdated={loadData}
        />
      )}

      {tabValue === 3 && isCoach && (
        <CoachRedemptionsTab
          teamId={userTeams.length > 0 ? userTeams[0].id : null}
          onRedemptionUpdated={loadData}
        />
      )}

      {/* Quick Status Summary for Athletes */}
      {user?.role === 'athlete' && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Prize Redemption Status
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>How it works:</strong> When you redeem a prize, your coach will review and approve it. 
              You'll see status updates in the "My Redemptions" tab above.
            </Typography>
          </Alert>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              icon={<PendingIcon />} 
              label="Pending: Waiting for coach approval" 
              color="warning" 
              variant="outlined" 
            />
            <Chip 
              icon={<ApprovedIcon />} 
              label="Approved: Coach will fulfill soon" 
              color="success" 
              variant="outlined" 
            />
            <Chip 
              icon={<FulfilledIcon />} 
              label="Fulfilled: Prize delivered!" 
              color="info" 
              variant="outlined" 
            />
          </Box>
        </Box>
      )}

      {isCoach && userTeams.length > 0 && (
        <CreatePrizeDialog
          open={createPrizeOpen}
          onClose={() => setCreatePrizeOpen(false)}
          onPrizeCreated={handlePrizeCreated}
          teamId={userTeams[0].id}
          coachId={user.id}
        />
      )}

      {/* Contest Rules and Official Rules Section - Always visible */}
      <Card sx={{ mt: 4, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              cursor: 'pointer'
            }}
            onClick={() => setContestRulesOpen(!contestRulesOpen)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon color="primary" />
              <Typography variant="h6" component="h2">
                Official Contest Rules & Disclaimer
              </Typography>
            </Box>
            <IconButton size="small">
              {contestRulesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={contestRulesOpen}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Official Contest Rules
              </Typography>
              
              <Typography variant="body2" paragraph sx={{ mt: 2 }}>
                <strong>1. Eligibility:</strong> This contest is open to all registered users of the Streaker app who are members of a team. 
                Employees, officers, and directors of the contest sponsor and their immediate families are not eligible to participate.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>2. How to Enter:</strong> Participants earn points through logging daily activities including running, workouts, cross-training, 
                and health metrics (sleep, hydration, nutrition). Points can be redeemed for prizes available in the Individual Prizes and Team Prizes sections.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>3. Prize Redemption:</strong> Prizes are redeemed using points earned through app activities. Each prize requires a specified number 
                of points. Once redeemed, the participant's coach must approve the redemption before the prize can be fulfilled. Prize availability is subject 
                to change without notice.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>4. Prize Fulfillment:</strong> Approved prize redemptions will be fulfilled by the participant's coach or team administrator. 
                Fulfillment timeframes may vary. The app sponsor is not responsible for prizes that are not fulfilled by team administrators.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>5. Prize Limitations:</strong> Prizes are subject to availability. No cash equivalent or substitution of prizes is permitted. 
                Prizes are non-transferable. The app sponsor reserves the right to substitute prizes of equal or greater value.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>6. Point Earning:</strong> Points are earned based on activity logging and timely submissions. Points may be deducted or adjusted 
                at the discretion of team administrators for invalid or fraudulent activity submissions.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>7. Contest Period:</strong> This contest is ongoing and has no specific end date. Points may be earned and prizes redeemed at any 
                time while the contest is active. The contest sponsor reserves the right to modify or terminate the contest at any time.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>8. Disqualification:</strong> Participants who engage in fraudulent activity, violate app terms of service, or engage in any 
                conduct that the contest sponsor deems inappropriate may be disqualified from the contest and forfeit any points or prizes.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>9. Limitation of Liability:</strong> By participating in this contest, participants agree to release and hold harmless the contest 
                sponsor, its officers, directors, employees, and agents from any and all liability, claims, or actions of any kind arising from or in connection 
                with participation in the contest or acceptance, possession, or use of any prize.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>10. Privacy:</strong> Information collected from participants will be used in accordance with the app's Privacy Policy. 
                By participating, participants consent to the collection and use of their information as described in the Privacy Policy.
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Apple Disclaimer
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  This contest is not sponsored, endorsed, or administered by Apple Inc. Apple Inc. is not involved in any way with this contest or sweepstakes. 
                  Apple Inc. has no responsibility or liability for this contest or sweepstakes. All questions, comments, or complaints regarding this contest 
                  should be directed to the contest sponsor, not Apple Inc.
                </Typography>
              </Alert>

              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 2 }}>
                For questions about this contest, please contact your team coach or administrator. 
                Contest rules are subject to change at any time. It is the participant's responsibility to review these rules regularly.
              </Typography>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Success Notification */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />
    </Box>
  );
};

export default PrizesPage;
